// src/pages/ContentAnalyzer.tsx
import React, { useState, useEffect } from "react";
import {
  Card,
  Input,
  Row,
  Col,
  Statistic,
  Table,
  Typography,
  Progress,
  Tag,
} from "antd";
import {
  FontSizeOutlined,
  ClockCircleOutlined,
  FileWordOutlined,
  AlignLeftOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Text } = Typography;

const ContentAnalyzer: React.FC = () => {
  const [text, setText] = useState("");
  const [stats, setStats] = useState({
    words: 0,
    chars: 0,
    sentences: 0,
    readTime: 0,
    density: [] as any[],
  });

  useEffect(() => {
    if (!text.trim()) {
      setStats({ words: 0, chars: 0, sentences: 0, readTime: 0, density: [] });
      return;
    }

    const stopWords = new Set([
      "the",
      "is",
      "in",
      "and",
      "to",
      "of",
      "a",
      "for",
      "on",
      "with",
      "as",
      "by",
      "at",
      "an",
      "be",
      "this",
      "that",
      "it",
      "from",
      "or",
      "are",
      "was",
      "were",
    ]);

    // 1. Basic Counts
    const words = text.trim().split(/\s+/).length;
    const chars = text.length;
    const sentences = text.split(/[.!?]+/).filter(Boolean).length;

    // 2. Reading Time (Avg 200 wpm)
    const readTime = Math.ceil(words / 200);

    // 3. Keyword Density
    const cleanText = text
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .split(/\s+/);

    const frequency: Record<string, number> = {};
    cleanText.forEach((word) => {
      if (word.length > 3 && !stopWords.has(word)) {
        frequency[word] = (frequency[word] || 0) + 1;
      }
    });

    const density = Object.entries(frequency)
      .map(([word, count]) => ({
        word,
        count,
        percent: ((count / words) * 100).toFixed(1),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8); // Top 8 keywords

    setStats({ words, chars, sentences, readTime, density });
  }, [text]);

  const columns = [
    { title: "Keyword", dataIndex: "word", key: "word" },
    { title: "Count", dataIndex: "count", key: "count" },
    {
      title: "Density",
      dataIndex: "percent",
      key: "percent",
      render: (val: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress
            percent={parseFloat(val)}
            steps={5}
            size="small"
            strokeColor="#3b82f6"
            showInfo={false}
          />
          <Text type="secondary">{val}%</Text>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: any) => {
        const p = parseFloat(record.percent);
        if (p > 4) return <Tag color="red">Over-optimized</Tag>;
        if (p > 1) return <Tag color="green">Good</Tag>;
        return <Tag color="default">Natural</Tag>;
      },
    },
  ];

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={16}>
        <Card className="glass-card" bordered={false} title="Content Editor">
          <TextArea
            placeholder="Paste your article or blog post here to analyze SEO factors..."
            rows={20}
            style={{
              fontSize: 16,
              padding: 20,
              borderRadius: 8,
              border: "none",
              background: "rgba(0,0,0,0.02)",
            }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            showCount
          />
        </Card>
      </Col>

      <Col xs={24} lg={8}>
        <Row gutter={[16, 16]}>
          {/* Quick Stats Grid */}
          <Col span={12}>
            <Card bordered={false} className="glass-card">
              <Statistic
                title="Words"
                value={stats.words}
                prefix={<AlignLeftOutlined style={{ color: "#3b82f6" }} />}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card bordered={false} className="glass-card">
              <Statistic
                title="Reading Time"
                value={`${stats.readTime} min`}
                prefix={<ClockCircleOutlined style={{ color: "#8b5cf6" }} />}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card bordered={false} className="glass-card">
              <Statistic
                title="Sentences"
                value={stats.sentences}
                prefix={<FileWordOutlined style={{ color: "#10b981" }} />}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card bordered={false} className="glass-card">
              <Statistic
                title="Characters"
                value={stats.chars}
                prefix={<FontSizeOutlined style={{ color: "#f59e0b" }} />}
              />
            </Card>
          </Col>

          {/* Keyword Density Table */}
          <Col span={24}>
            <Card
              title="Keyword Density"
              bordered={false}
              className="glass-card"
              bodyStyle={{ padding: 0 }}
            >
              <Table
                dataSource={stats.density}
                columns={columns}
                rowKey="word"
                pagination={false}
                size="small"
                className="safe-text"
              />
            </Card>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default ContentAnalyzer;
